import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'

import { fetchUserSettings } from 'src/Libs/user/api/user.api'
import { useTypedSelector } from 'src/State/RootReducer'
import { updateFeatureContentVersion } from '../apis/update-feature-content-version'
import { UserSettings } from 'src/Libs/user/types'
import {
  FeatureContentElementKey,
  FeatureContentVersions,
  FeatureContentVersionType,
  INITIAL_FEATURE_CONTENT_VERSION,
  PARENT_FEATURE_ELEMENTS,
} from '../feature-versioning'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'

interface ReturnType {
  currentFeatureContentVersion: FeatureContentVersionType
  handleUpdateContentVersion: (elementsToUpdate: FeatureContentElementKey[]) => Promise<void>
}

/**
 * Exposes the current feature versions and allows to update them.
 */
export const useHasNewFeature = (): ReturnType => {
  const authFetch = useAuthFetchWithResponse()
  const userId = useTypedSelector(state => state.auth.userId)

  const { data, error, isError, isSuccess, refetch } = usePortalQuery({
    queryKey: ['hasNewFeature', userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error('No userId available')
      }
      const result = await fetchUserSettings(userId, authFetch)

      if (result?.value) {
        return result.value
      } else {
        throw new Error('Failed to fetch user settings')
      }
    },
    enabled: !!userId,
  })

  useEffect(() => {
    if (isError) {
      console.error(error)
      return
    }

    if (isSuccess && data) {
      const areUserSettingsInitializedWithNewFeatureContent = data.featureContentVersion
      if (!areUserSettingsInitializedWithNewFeatureContent && userId) {
        updateFeatureContentVersion(userId, INITIAL_FEATURE_CONTENT_VERSION, authFetch)
      }

      const isNewElementKeyAdded =
        data.featureContentVersion && Object.keys(data.featureContentVersion).length !== Object.keys(FeatureContentVersions).length

      if (isNewElementKeyAdded && userId && data.featureContentVersion) {
        addVersionForNewFeatureElement(userId, data)
      }
    }
  }, [data, error, isSuccess, isError, userId]) //eslint-disable-line

  const mutateElementsWithNewFeatures = usePortalMutation({
    mutationFn: ({ userId, featureContentVersion }: { userId: number; featureContentVersion: FeatureContentVersionType }) =>
      updateFeatureContentVersion(userId, featureContentVersion, authFetch),
  })

  /**
   * If we add new feature elements to the code they are not present in the user settings.
   * This function adds the new elements to the user settings with the initial version being 0.
   */
  const addVersionForNewFeatureElement = async (userId: number, data: UserSettings): Promise<void> => {
    const newElementKeys = Object.keys(FeatureContentVersions).filter(key => !Object.keys(data?.featureContentVersion ?? {}).includes(key))
    const newElements: FeatureContentVersionType = newElementKeys.reduce((acc, key) => {
      acc[key as FeatureContentElementKey] = 0
      return acc
    }, {} as FeatureContentVersionType)
    const updatedElements = { ...data.featureContentVersion, ...newElements }
    await updateFeatureContentVersion(userId, updatedElements, authFetch)
  }

  /**
   * Update the content version of a single element. For parent elements, which contain multiple child elements, do not update.
   * example parent and child element: property side bar element -> solar side bar element, wallbox side bar element, powermeter side bar element, ...
   * @param elementsToUpdate
   */
  const handleUpdateContentVersion = async (elementsToUpdate: FeatureContentElementKey[]): Promise<void> => {
    if (!userId) {
      console.error('userId is required to update content version')
      return
    }

    const userSettingsCopy = { ...data }

    if (isEmpty(userSettingsCopy)) {
      const newUserSettings = { featureContentVersion: { ...FeatureContentVersions } }
      mutateElementsWithNewFeatures.mutate({ userId, featureContentVersion: newUserSettings.featureContentVersion })
      refetch()
    }

    const isParentFeatureElement = PARENT_FEATURE_ELEMENTS.some(parentElement => elementsToUpdate.includes(parentElement))
    if (isParentFeatureElement) return

    elementsToUpdate.forEach(element => {
      if (userSettingsCopy.featureContentVersion) {
        userSettingsCopy.featureContentVersion[element] = FeatureContentVersions[element]
        mutateElementsWithNewFeatures.mutate({ userId, featureContentVersion: userSettingsCopy.featureContentVersion })
      }
    })
  }

  return {
    currentFeatureContentVersion: data?.featureContentVersion ?? INITIAL_FEATURE_CONTENT_VERSION,
    handleUpdateContentVersion,
  }
}
